import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TablePagination,
  TextField,
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Page from '../../../components/Page';
import Loader from '../../../components/Loader';
import {
  getPurchaseHistory,
  PurchaseHistoryItem,
  PurchaseHistoryResponse,
} from '../../../services/company-credits';
import {
  processCreditExpiration,
  checkLicenseExpiration,
  suspendCompaniesAfterGracePeriod,
  processAllExpirations,
} from '../../../services/expiration-management';
import { toastHandler } from '../../../utils/toastHandler';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useAppSelector } from '../../../store';
import { getCompaniesList } from '../../../services/companies-list';

const PurchaseHistory = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PurchaseHistoryItem[]>([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [selectedCompany, setSelectedCompany] = useState<string | null>(null);
  const [companies, setCompanies] = useState<any[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [processingCredit, setProcessingCredit] = useState(false);
  const [processingLicense, setProcessingLicense] = useState(false);
  const [processingSuspension, setProcessingSuspension] = useState(false);
  const [processingAll, setProcessingAll] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<string | null>(null);
  const userRole = useAppSelector((state) => state.auth.user.userRole);
  const isAdmin = userRole === 'admin' || userRole === 'adminTier2' || userRole === 'adminTier3';

  // Fetch companies list for admin
  useEffect(() => {
    if (isAdmin) {
      const fetchCompanies = async () => {
        try {
          setLoadingCompanies(true);
          const response = await getCompaniesList({ limit: 1000 });
          setCompanies(response.data || []);
        } catch (error: any) {
          console.error('Error fetching companies:', error);
          // Only show toast if it's a real error, not just empty response
          if (error?.message) {
            toastHandler(error);
          }
        } finally {
          setLoadingCompanies(false);
        }
      };
      fetchCompanies();
    }
  }, [isAdmin]);

  // Fetch purchase history
  const fetchData = async () => {
    try {
      setLoading(true);
      const response: PurchaseHistoryResponse = await getPurchaseHistory({
        page: page + 1,
        limit,
        companyId: selectedCompany || undefined,
      });
      setData(response.data);
      setTotal(response.total);
    } catch (error: any) {
      console.error('Error fetching purchase history:', error);
      toastHandler(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, limit, selectedCompany]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'COMPLETED':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'FAILED':
      case 'CANCELLED':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleProcessCreditExpiration = async () => {
    try {
      setProcessingCredit(true);
      const result = await processCreditExpiration();
      toast.success(
        `Credit expiration processed: ${result.expiredPackages} packages, ${result.totalExpiredCredits} credits expired`
      );
    } catch (error: any) {
      console.error('Error processing credit expiration:', error);
    } finally {
      setProcessingCredit(false);
      setConfirmDialogOpen(false);
      setConfirmAction(null);
    }
  };

  const handleCheckLicenseExpiration = async () => {
    try {
      setProcessingLicense(true);
      await checkLicenseExpiration();
      toast.success('License expiration check completed successfully');
    } catch (error: any) {
      console.error('Error checking license expiration:', error);
    } finally {
      setProcessingLicense(false);
      setConfirmDialogOpen(false);
      setConfirmAction(null);
    }
  };

  const handleSuspendCompanies = async () => {
    try {
      setProcessingSuspension(true);
      await suspendCompaniesAfterGracePeriod();
      toast.success('Company suspension after grace period completed successfully');
    } catch (error: any) {
      console.error('Error suspending companies:', error);
    } finally {
      setProcessingSuspension(false);
      setConfirmDialogOpen(false);
      setConfirmAction(null);
    }
  };

  const handleProcessAll = async () => {
    try {
      setProcessingAll(true);
      const result = await processAllExpirations();
      toast.success('All expiration processes completed successfully');
      console.log('Process all results:', result);
    } catch (error: any) {
      console.error('Error processing all expirations:', error);
    } finally {
      setProcessingAll(false);
      setConfirmDialogOpen(false);
      setConfirmAction(null);
    }
  };

  // const handleButtonClick = (action: string) => {
  //   setConfirmAction(action);
  //   setConfirmDialogOpen(true);
  // };

  const handleConfirm = () => {
    if (confirmAction === 'credit') {
      handleProcessCreditExpiration();
    } else if (confirmAction === 'license') {
      handleCheckLicenseExpiration();
    } else if (confirmAction === 'suspend') {
      handleSuspendCompanies();
    } else if (confirmAction === 'all') {
      handleProcessAll();
    }
  };

  const getConfirmMessage = () => {
    switch (confirmAction) {
      case 'credit':
        return 'Are you sure you want to process credit expiration? This will expire unused credits from expired packages.';
      case 'license':
        return 'Are you sure you want to check license expiration? This will send notifications to companies with expiring licenses.';
      case 'suspend':
        return 'Are you sure you want to suspend companies after grace period? This will suspend companies whose licenses expired 90+ days ago.';
      case 'all':
        return 'Are you sure you want to process all expiration tasks? This will run credit expiration, license checking, and company suspension.';
      default:
        return 'Are you sure?';
    }
  };

  if (loading && data.length === 0) {
    return <Loader />;
  }

  return (
    <Page title="Purchase History">
      <Container maxWidth="xl">
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h4" gutterBottom>
            Purchase History
          </Typography>

          {/* Expiration Management Buttons for Admin */}
          {/* {isAdmin && (
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <LoadingButton
                variant="outlined"
                color="warning"
                startIcon={<Iconify icon="eva:refresh-outline" />}
                onClick={() => handleButtonClick('credit')}
                loading={processingCredit}
                disabled={processingCredit || processingLicense || processingSuspension || processingAll}
              >
                Process Credit Expiration
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                color="info"
                startIcon={<Iconify icon="eva:clock-outline" />}
                onClick={() => handleButtonClick('license')}
                loading={processingLicense}
                disabled={processingCredit || processingLicense || processingSuspension || processingAll}
              >
                Check License Expiration
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                color="error"
                startIcon={<Iconify icon="eva:alert-circle-outline" />}
                onClick={() => handleButtonClick('suspend')}
                loading={processingSuspension}
                disabled={processingCredit || processingLicense || processingSuspension || processingAll}
              >
                Suspend Companies
              </LoadingButton>
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<Iconify icon="eva:play-circle-outline" />}
                onClick={() => handleButtonClick('all')}
                loading={processingAll}
                disabled={processingCredit || processingLicense || processingSuspension || processingAll}
              >
                Process All
              </LoadingButton>
            </Stack>
          )} */}
        </Box>

        {/* Company Filter for Admin */}
        {isAdmin && (
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              options={companies}
              getOptionLabel={(option) => `${option.name} (${option.email})`}
              value={companies.find((c) => c.id === selectedCompany) || null}
              onChange={(event, newValue) => {
                setSelectedCompany(newValue?.id || null);
                setPage(0);
              }}
              loading={loadingCompanies}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Filter by Company"
                  placeholder="Select a company or leave empty to see all"
                  variant="outlined"
                  fullWidth
                />
              )}
              sx={{ maxWidth: 400 }}
            />
          </Box>
        )}

        <Card>
          <CardContent>
            <TableContainer sx={{ width: '100%', overflowX: 'auto' }}>
              <Table sx={{ minWidth: 800 }}>
                <TableHead>
                  <TableRow>
                    {isAdmin && <TableCell>Company</TableCell>}
                    <TableCell>Package Name</TableCell>
                    <TableCell>Credits</TableCell>
                    <TableCell>Price</TableCell>
                    <TableCell>Purchased At</TableCell>
                    <TableCell>Expires At</TableCell>
                    <TableCell>Expired</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 8 : 7} align="center">
                        <Typography variant="body2" color="text.secondary">
                          No purchase history found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    data
                      .map((item) => {
                        const displayStatus = (item.paymentStatus?.toUpperCase() === 'PAID' || item.paymentStatus?.toUpperCase() === 'COMPLETED') ? 'COMPLETED' : 'FAILED';
                        return (
                        <TableRow key={item.id}>
                          {isAdmin && (
                            <TableCell>
                              {item.companyName || 'N/A'}
                              {item.companyEmail && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {item.companyEmail}
                                </Typography>
                              )}
                            </TableCell>
                          )}
                          <TableCell>{item.packageName}</TableCell>
                          <TableCell>{item.creditCount}</TableCell>
                          <TableCell>
                            {typeof item.price === 'number'
                              ? item.price.toFixed(2)
                              : parseFloat(item.price || '0').toFixed(2)}
                          </TableCell>
                          <TableCell>
                            {moment(item.purchasedAt).format('YYYY-MM-DD HH:mm')}
                          </TableCell>
                          <TableCell>
                            {item.expiresAt
                              ? moment(item.expiresAt).format('YYYY-MM-DD')
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.isExpired ? 'Yes' : 'No'}
                              color={item.isExpired ? 'error' : 'success'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={displayStatus}
                              color={getStatusColor(displayStatus) as any}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        );
                      })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={limit}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50, 100]}
            />
          </CardContent>
        </Card>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={() => {
            setConfirmDialogOpen(false);
            setConfirmAction(null);
          }}
        >
          <DialogTitle>Confirm Action</DialogTitle>
          <DialogContent>
            <DialogContentText>{getConfirmMessage()}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setConfirmDialogOpen(false);
                setConfirmAction(null);
              }}
            >
              Cancel
            </Button>
            <LoadingButton
              onClick={handleConfirm}
              variant="contained"
              color="primary"
              loading={
                processingCredit || processingLicense || processingSuspension || processingAll
              }
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
};

export default PurchaseHistory;

